import { Koi } from '@ksyos/koi';
import type { TypedReadSchema } from '@common/formEngine/typedReadSchema.types';
import type { ProcessActivityAsset } from '@common/validation/processActivityAsset';
import type { TypedSchema } from '@common/validation/utils/typedSchemaType';
import { Vervolgstap } from '@common/customControls/bariatrics';
import { getProcessActivityAssetsSchema } from '@common/validation/processActivityAsset';
import { addAllowNull } from '@common/validation/utils/addAllowNull';
import { getTextAreaStringSchema } from '@common/validation/utils/getTextAreaStringSchema';

export const bariPostPoliReportVervolgstappen = [
    Vervolgstap.lifestylecoach_inschakelen,
    Vervolgstap.vs_inschakelen,
    Vervolgstap.geen_actie_nodig,
] as const;

export type BariPostPoliReportVervolgstap = (typeof bariPostPoliReportVervolgstappen)[number];

export type BariPostPoliReportData = {
    assets: ProcessActivityAsset[];
    hulpvraag: string | null;
    hulpvraag_aan_expert: string | null;
    interventie: string | null;
    verslag: string | null;
    vervolgstap: BariPostPoliReportVervolgstap | null;
};

const bariPostPoliReportFinal: TypedSchema<BariPostPoliReportData> = {
    assets: getProcessActivityAssetsSchema(),
    hulpvraag: getTextAreaStringSchema(true),
    hulpvraag_aan_expert: Koi.when('vervolgstap', {
        is: Koi.valid(...[Vervolgstap.vs_inschakelen, Vervolgstap.lifestylecoach_inschakelen]),
        otherwise: Koi.valid(null),
        then: getTextAreaStringSchema(),
    }),
    interventie: getTextAreaStringSchema(true),
    verslag: getTextAreaStringSchema(),
    vervolgstap: Koi.koi().valid(...bariPostPoliReportVervolgstappen),
};

const bariPostPoliReportDraft = addAllowNull(bariPostPoliReportFinal, ['assets', 'hulpvraag', 'interventie']);

export const bariPostPoliReportDraftSchema = Koi.object(bariPostPoliReportDraft);
export const bariPostPoliReportFinalSchema = Koi.object(bariPostPoliReportFinal);

export type BariPostPoliReportReadKey = keyof BariPostPoliReportData;

export type BariPostPoliReportReadLabel =
    | 'Bestand(en)'
    | 'Hulpvraag'
    | 'Interventie'
    | 'Verslag'
    | 'Vervolgstap'
    | 'Hulpvraag aan expert';

export type BariPostPoliReportReadSectionTitle = 'Verslag' | 'Vervolgstap';

export const bariPostPoliReportReadSchema: TypedReadSchema<
    BariPostPoliReportReadKey,
    BariPostPoliReportReadLabel,
    BariPostPoliReportReadSectionTitle
>[] = [
    {
        items: [
            {
                key: 'verslag',
                label: 'Verslag',
            },
            {
                key: 'hulpvraag',
                label: 'Hulpvraag',
            },
            {
                key: 'interventie',
                label: 'Interventie',
            },
            {
                key: 'assets',
                label: 'Bestand(en)',
            },
        ],
        sectionTitle: 'Verslag',
    },
    {
        items: [
            {
                key: 'vervolgstap',
                label: 'Vervolgstap',
            },
            {
                key: 'hulpvraag_aan_expert',
                label: 'Hulpvraag aan expert',
            },
        ],
        sectionTitle: 'Vervolgstap',
    },
];
